"use client";

import React, { useEffect } from "react";
import { useMemoizedFn } from "ahooks";
import { useSnapshot } from "valtio";
import log from "logbeacon";
import { cn, pubsub } from "utils";
import { deviceDetector } from "@ospweb/sdk-ui";
import { ChatRefContent } from "../../Sender/type";
import type { Question } from "../../Sender/QuestionItem";
import { IChatInfo } from "../../ChatModal";
import EdgenDrawerCallable from "../../EdgenDrawerCallable";
import type { SenderSubmitOptions } from "../../Sender/type";
import { CopilotSenderTabInput } from "./CopilotSenderTabInput";
import Translations from "@translate/components/CoPilot/CopilotAgent/CopilotSender";
import { I18nUtil } from "@utils/i18n";
import { HomePageSender } from "@components";
import { ChatStore, rootStore } from "@/src/store";
import {
  useIsPc,
  useOpenChatModalWithClose,
  useRecommendQuestion,
} from "@/src/hooks";
import { useRecommendQuestionTracking } from "@/src/hooks/useRecommendQuestionTracking";
import { encodeString } from "@/src/utils/stringEncoder";
import { pubSubKeyMap } from "@/src/utils/pubSubKeyMap";
import { SCOPE_MAP } from "@/src/const";
import { LOCAL_STORAGE_KEYS } from "@/src/config";
import { getRandomElements } from "@/src/utils";

const I18n = I18nUtil.createScoped(Translations);

interface CopilotSenderProps {
  /** 推荐问题列表 */
  questions?: Question[];
  /** 千人千面 chat tab 问题列表 */
  chatQuestion?: Question[];
  defaultPlaceholder?: string;
  className?: string;
  readOnly?: boolean;
  withQuestions?: boolean;
  expandClassName?: string;
}
export const setCopilotSenderChatRefContent = (
  content: ChatRefContent | null,
) => {
  pubsub.emit(pubSubKeyMap.COPILOT_SENDER_SET_CHAT_REF_CONTENT, content);
};

const useStopChatStream = () => {
  const { stop, streamMessage, loading } = useSnapshot(ChatStore);
  const stopChatStream = useMemoizedFn(async () => {
    await stop?.({
      stopParams: {
        messageId: streamMessage?.messageId || streamMessage?.id,
      },
    });
  });
  return {
    stopChatStream,
    loading,
  };
};

export const useCopilotSenderTabInputQuestions = () => {
  const { user } = rootStore;
  const { ownerInfo } = useSnapshot(user);
  const scopeKey = SCOPE_MAP.AGENT_SEARCH;
  // 获取格式化后的推荐问题（已根据当前语言处理）
  const { questions, isLoading: questionsLoading } = useRecommendQuestion(
    {
      [`scopes[${scopeKey}]`]: 20,
      related: ownerInfo?.id as any,
    },
    {
      key: scopeKey,
    },
  );

  return {
    questions,
    questionsLoading,
  };
};
/**
 * CopilotSender 组件
 * 封装了 CopilotAgent 中的 Sender 相关逻辑
 * 通过 PubSub 事件总线接收外部设置的 chatRefContent
 */
export const CopilotSender: React.FC<CopilotSenderProps> = ({
  questions,
  chatQuestion,
  defaultPlaceholder,
  readOnly,
  className,
  withQuestions = true,
  expandClassName,
}) => {
  const { handleOpenChatModal, onCloseClick } = useOpenChatModalWithClose();
  const { loading } = useStopChatStream();

  // 聊天引用内容存储在 ref，确保值实时变化且不引起组件重渲染
  const chatRefContentRef = React.useRef<ChatRefContent | null>(null);
  const [isPC] = useIsPc();

  // 使用推荐问题跟踪 hook
  const {
    value,
    setValue,
    handleChange,
    handleQuestionSelect,
    shouldUseRecommendInfo,
    getRecommendInfo,
    reset: resetTracking,
  } = useRecommendQuestionTracking();

  // 占位符映射
  const placeMap = {
    ALERT: I18n.t("Want a deeper dive into this signal?"),
    SUMMARY: I18n.t("Ask me to get more clarity on your positions."),
    DEFAULT: I18n.t("What do you want to explore together today?"),
  };

  // 处理发送问题
  const handleSend = useMemoizedFn(
    async (question?: string, submitOptions?: SenderSubmitOptions) => {
      log.debug("[文案]question", question);
      // 构建发送的问题文本，只有当有有效的引用时才添加引用格式
      let finalQuestion = question;
      if (
        chatRefContentRef.current &&
        (chatRefContentRef.current.refId || chatRefContentRef.current.link)
      ) {
        finalQuestion = `[${chatRefContentRef.current.title || ""}](referenceId=${chatRefContentRef.current.refId || chatRefContentRef.current.link})\n${question}`;
      }
      let requestParams = {
        question: finalQuestion,
        questions: getRandomElements(questions || [], isPC ? 6 : 4),
        customParams: {
          attachments: submitOptions?.attachments,
          voiceContext: submitOptions?.voiceContext,
        },
        autoSubmit: chatRefContentRef.current?.autoSubmit ?? true,
      } as IChatInfo & { questions?: Question[] };

      const isRecommend = shouldUseRecommendInfo(question);

      // 如果用户选中了推荐问题且没有修改输入，传递推荐问题信息
      if (isRecommend) {
        const selectedQuestion = getRecommendInfo();
        if (selectedQuestion) {
          const sign = `content_${encodeString(`chat:id#${selectedQuestion.source_id}`)}`;
          requestParams = {
            ...requestParams,
            sign,
            cacheQuestion: selectedQuestion.question,
          };
        }
      }
      log.info(
        `[onChartClick][trace]${isRecommend ? "[recommend]" : ""}[sender]`,
        requestParams,
        chatRefContentRef.current,
      );
      // loading的过程中，新发起对话，需要结束上一个弹窗，再新开一个对话。
      if (loading) {
        await EdgenDrawerCallable.end(false);
        ChatStore.hasChatModal = false;
        await localStorage.removeItem(LOCAL_STORAGE_KEYS.CHAT_ID);
      }
      setTimeout(
        () => {
          handleOpenChatModal(requestParams);
          // 清空状态
          chatRefContentRef.current = null;
          resetTracking();
        },
        loading ? 500 : 0,
      );
    },
  );

  // 监听 PubSub 事件，接收外部设置的 chatRefContent
  useEffect(() => {
    const unsubscribe = pubsub.on(
      pubSubKeyMap.COPILOT_SENDER_SET_CHAT_REF_CONTENT,
      (content: ChatRefContent | null) => {
        log.debug("[CopilotSender] 收到设置 chatRefContent 事件", content);
        chatRefContentRef.current = content;
        if (content?.question) {
          // 直接自动发送
          handleSend(content.question);
        } else {
          setValue("");
        }
      },
    );

    const unsubscribeClose = pubsub.on(
      pubSubKeyMap.COPILOT_SENDER_CLOSE_CHAT,
      () => {
        onCloseClick();
        chatRefContentRef.current = null;
        setValue("");
      },
    );

    return () => {
      unsubscribe?.();
      unsubscribeClose?.();
    };
  }, [onCloseClick, handleSend, setValue]);

  // 处理关闭聊天 Toast
  const handleCloseChatToast = useMemoizedFn(() => {
    log.debug("[文案]onCloseChatToast");
    chatRefContentRef.current = null;
  });

  // h5的情况下，有引用 & 有问题时展示输入框，否则展示 TabInput（推荐问题轮询）
  return !deviceDetector.isMobile() ||
    value ||
    chatRefContentRef.current?.refId ? (
    <div className={cn("w-full", className)} data-tracking-scope="sender">
      <HomePageSender
        onChange={handleChange}
        onSend={handleSend}
        onQuestionSelect={handleQuestionSelect}
        value={value}
        data={questions}
        readOnly={
          !!chatRefContentRef.current?.question || !!value ? false : readOnly
        }
        placeholder={
          placeMap[chatRefContentRef.current?.type as keyof typeof placeMap] ||
          defaultPlaceholder ||
          placeMap.DEFAULT
        }
        withQuestions={withQuestions}
        chatRefContent={chatRefContentRef.current as ChatRefContent}
        onCloseChatToast={handleCloseChatToast}
        expandClassName={expandClassName}
        style={
          {
            // boxShadow: "0 0 80px 0 #000",
          }
        }
      />
    </div>
  ) : (
    <CopilotSenderTabInput questions={questions} chatQuestion={chatQuestion} />
  );
};

CopilotSender.displayName = "CopilotSender";
